import type {
  EstimateAssumption,
  EstimateExclusion,
  EstimateWarning,
  EstimatorInput,
  Money,
  PricingCatalog,
  PricingLineItem
} from "../../types/index.js";
import { calculateLineItemCost, type LineItemCalculationResult } from "../pricing-calculator.js";

export interface AppliedLineItem {
  item: PricingLineItem;
  result: LineItemCalculationResult;
}

export interface ProjectCalculationOutput {
  applied: AppliedLineItem[];
  assumptions: EstimateAssumption[];
  exclusions: EstimateExclusion[];
  /** True if the anchor/base line item (e.g. base_renovation) could not be computed at all, e.g. due to missing room size. */
  anchorMissing: boolean;
  /**
   * Project-calculator-specific warnings, e.g. flagging a contradictory
   * pair of selections or a requested scope category that had no
   * applicable pricing in the catalog. Optional - most calculators won't
   * need any - and merged into the engine's overall `warnings` array
   * alongside the input-validation and structural/permit warnings that
   * already exist there.
   */
  warnings?: EstimateWarning[];
}

export type ProjectCalculatorFn = (
  input: EstimatorInput,
  catalog: PricingCatalog,
  regionalMultiplier: number
) => ProjectCalculationOutput;

export function findItem(catalog: PricingCatalog, id: string): PricingLineItem | undefined {
  return catalog.items.find((i) => i.id === id);
}

/**
 * Computes and records one line item's cost if the item exists in the
 * catalog. Returns null (and adds nothing) when the item id isn't found -
 * this is how a project calculator stays safe against a catalog that
 * hasn't been populated with every item it references yet, rather than
 * throwing.
 */
export function applyItem(
  catalog: PricingCatalog,
  itemId: string,
  params: Parameters<typeof calculateLineItemCost>[1]
): AppliedLineItem | null {
  const item = findItem(catalog, itemId);
  if (!item) return null;
  const result = calculateLineItemCost(item, params);
  return { item, result };
}

/**
 * Kitchens run roughly this many linear feet of base + upper cabinetry
 * per square foot of kitchen floor area, for a typical galley/L-shaped
 * layout. This is a planning-level heuristic - not derived from any
 * source - used only because the engine needs a linear-footage quantity
 * for the cabinetry line item and the structured input only captures
 * square footage. Always surfaced as an explicit assumption in the
 * result (see kitchen.ts) rather than applied silently.
 */
export const KITCHEN_CABINETRY_LINEAR_FT_PER_SQFT = 0.16;

export function deriveKitchenCabinetryLinearFeet(sqft: number): number {
  return Math.max(8, Math.round(sqft * KITCHEN_CABINETRY_LINEAR_FT_PER_SQFT));
}

/** Scales an already-computed applied line item's dollar figures by a flat factor (e.g. a scope multiplier applied only to the anchor item). */
export function scaleAppliedItem(applied: AppliedLineItem, factor: number): AppliedLineItem {
  const scale = (m: Money | null): Money | null => (m ? { amount: Math.round(m.amount * factor), currency: "CAD" } : null);
  return {
    item: applied.item,
    result: {
      ...applied.result,
      low: scale(applied.result.low),
      typical: scale(applied.result.typical),
      high: scale(applied.result.high),
      minimum: scale(applied.result.minimum),
      maximum: scale(applied.result.maximum)
    }
  };
}
